import { KeyboardEvent, ReactNode, RefObject, useCallback, useEffect, useId, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Modal — dialog accesible con backdrop, foco trap minimo y ESC para cerrar.
 *
 * Sigue DESIGN-SYSTEM §3.8:
 *  - Overlay violeta (primary-900/50) con fade.
 *  - Container cream-100 con scaleIn/fadeUp (respeta reduced-motion).
 *  - Slots: header (titulo + close), body (children), footer.
 *  - Bloquea scroll del body mientras esta abierto.
 *  - Atrapa el foco dentro del dialogo y devuelve al trigger al cerrar.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Titulo accesible (obligatorio). Si `hideTitle`, se mantiene solo para SR. */
  title: ReactNode;
  description?: ReactNode;
  /** Footer opcional (botones de accion). */
  footer?: ReactNode;
  size?: ModalSize;
  /** Si true, oculta el titulo visualmente pero queda en aria. */
  hideTitle?: boolean;
  /** Si true (default), un click en el backdrop cierra el modal. */
  closeOnOverlayClick?: boolean;
  /** Si true (default), la tecla ESC cierra el modal. */
  closeOnEscape?: boolean;
  /**
   * Opcional (CEC-261016-9b3b): elemento que recibe el foco al abrir. Sin él,
   * el foco va al primer elemento interactivo, como siempre.
   */
  initialFocusRef?: RefObject<HTMLElement>;
  children: ReactNode;
}

// Anchos max por talla siguiendo el design system.
const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-[480px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[800px]',
  xl: 'max-w-[1040px]',
};

// Selector de elementos enfocables dentro del modal (foco trap minimo).
const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  hideTitle = false,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  initialFocusRef,
  children,
}: ModalProps) {
  const reduceMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  // Refs latches: mantienen los callbacks/flags vivos SIN forzar al useEffect
  // a re-ejecutarse cuando el consumidor pasa una función inline como `onClose`
  // (caso común: `onClose={() => setX(null)}` en JSX). Si `onClose` estuviera
  // en el dep array, cada keystroke en cualquier input del modal causaría
  // re-render del padre → nuevo `onClose` → cleanup del effect → llamada a
  // `previouslyFocused.current?.focus?.()` que ROBA EL FOCO al input activo
  // (regresándolo al elemento que tenía foco antes de abrir el modal). El
  // patrón ref-latch evita el ciclo: el effect solo depende de `open`, así
  // su cleanup corre exclusivamente al cerrar.
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscapeRef.current = closeOnEscape;
  });

  // Bloqueo de scroll body + cierre por ESC. Solo depende de `open` para que
  // el cleanup (devolución de foco) corra SOLO al cerrar el modal.
  useEffect(() => {
    if (!open) return;

    // Guardamos el elemento que tenia foco para devolverlo al cerrar.
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: globalThis.KeyboardEvent) => {
      if (closeOnEscapeRef.current && event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      // Devolvemos el foco al trigger original (si sigue en DOM).
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Mueve foco al primer elemento interactivo al montar.
  useEffect(() => {
    if (!open) return;
    const node = dialogRef.current;
    if (!node) return;
    const focusables = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const first = focusables[0];
    // Si no hay nada enfocable, enfocamos el dialog mismo para que ESC funcione.
    (initialFocusRef?.current ?? first ?? node).focus({ preventScroll: true });
    // `initialFocusRef` es un objeto ref estable: no vuelve a correr el efecto.
  }, [open, initialFocusRef]);

  // Foco trap minimo: ciclar Tab dentro del modal.
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const node = dialogRef.current;
    if (!node) return;
    const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('disabled'),
    );
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  // Variants para entrada/salida.
  const backdropVariants = reduceMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const } },
        exit: { opacity: 0, transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] as const } },
      };

  const panelVariants = reduceMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.96, y: 12 },
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const },
        },
        exit: {
          opacity: 0,
          scale: 0.98,
          y: 8,
          transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] as const },
        },
      };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-modal" role="presentation">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-primary-900/50"
            aria-hidden="true"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
            onClick={() => closeOnOverlayClick && onClose()}
          />

          {/* Container que centra el panel */}
          <div className="relative flex min-h-full items-center justify-center p-4">
            <motion.div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              aria-describedby={description ? descriptionId : undefined}
              tabIndex={-1}
              onKeyDown={handleKeyDown}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={panelVariants}
              className={cn(
                'relative w-full bg-cream-100 text-primary-900',
                'rounded-lg shadow-elev3 overflow-hidden',
                'max-h-[90vh] flex flex-col',
                sizeClasses[size],
              )}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 p-6 border-b border-cream-200">
                <div className="min-w-0 flex-1">
                  <h2
                    id={titleId}
                    className={cn('text-h3 font-display text-primary-900', hideTitle && 'sr-only')}
                  >
                    {title}
                  </h2>
                  {description && (
                    <p id={descriptionId} className="mt-1 text-caption text-primary-900/70">
                      {description}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Cerrar"
                  className={cn(
                    'shrink-0 inline-flex items-center justify-center',
                    'h-8 w-8 rounded text-primary-900',
                    'hover:bg-cream-200 transition-colors duration-2 ease-out',
                  )}
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              {/* Body scrollable */}
              <div className="flex-1 overflow-y-auto p-6">{children}</div>

              {/* Footer opcional */}
              {footer && <div className="p-6 border-t border-cream-200 bg-cream-50">{footer}</div>}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
